import "./ProgressBar.css";
import { createMemo, type ParentComponent } from "solid-js";

type Props = {
	value?: number;
	/** Horizontal start position as a percentage of the track width (0–100). */
	offset?: number;
	id?: string;
	indeterminate?: boolean;
	className?: string;
	variant?: "success" | "warning" | "error" | "info" | "primary" | "purple";
};

/**
 * Horizontal progress bar showing completion percentage or indeterminate state.
 *
 * [Design System →](http://localhost:5174/component/ProgressBar)
 */
export const ProgressBar: ParentComponent<Props> = (props) => {
	const currentValue = createMemo(() => {
		if (props.indeterminate) return undefined;
		const value = props.value ?? 0;
		if (Number.isNaN(value)) return 0;
		return Math.min(100, Math.max(0, value));
	});

	const fillOffset = createMemo(() => {
		if (props.offset === undefined) return undefined;
		if (Number.isNaN(props.offset)) return 0;
		return Math.min(100, Math.max(0, props.offset));
	});

	const classes = createMemo(() =>
		[
			"gvtui-progress-bar",
			props.indeterminate || currentValue() === undefined
				? "indeterminate"
				: "",
			props.variant
				? `gvtui-progress-bar--${props.variant}`
				: "gvtui-progress-bar--info",
			props.className,
		]
			.filter(Boolean)
			.join(" "),
	);

	return (
		<div class={classes()}>
			<div
				class="gvtui-progress-bar-fill"
				style={{
					width:
						props.indeterminate || currentValue() === undefined
							? "50%"
							: `${currentValue()}%`,
					...(fillOffset() !== undefined ? { left: `${fillOffset()}%` } : {}),
				}}
			/>
		</div>
	);
};
